import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ChartCard } from '@/components/patterns/ChartCard'
import { tagVar } from '@/components/ui/tag-colors'
import { dayMonth } from './dates'
import { plural } from './text'
import type { ActivityTrend, ActivityWeek } from './types'

const SERIES = [
  { key: 'call', label: 'Gesprek', color: tagVar('blauw', 'deep') },
  { key: 'meeting', label: 'Meeting', color: tagVar('paars', 'deep') },
  { key: 'email', label: 'E-mail', color: tagVar('teal', 'deep') },
  { key: 'note', label: 'Notitie', color: tagVar('grijs', 'deep') },
] as const

type Point = { week: string; starts_on: string; call: number; meeting: number; email: number; note: number }

function points(weeks: ActivityWeek[]): Point[] {
  return weeks.map((week) => ({
    week: week.week,
    starts_on: week.starts_on,
    call: week.call,
    meeting: week.meeting,
    email: week.email + week.mail_in + week.mail_out, // logged and synchronised mail in one column
    note: week.note,
  }))
}

function trendText(trend: ActivityTrend): string {
  if (trend.is_new) return 'geen contact in de 12 weken ervoor'
  if (trend.change_pct === null) return 'geen vergelijking mogelijk'
  if (trend.change_pct === 0) return 'gelijk aan de 12 weken ervoor'
  const sign = trend.change_pct > 0 ? '+' : ''
  return `${sign}${trend.change_pct}% t.o.v. de 12 weken ervoor`
}

const reducedMotion = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Widget 6: contact moments per week, stacked per type (recharts), with the total and the trend (design §4.1). */
export function ActivityWidget({ weeks, trend }: { weeks: ActivityWeek[]; trend: ActivityTrend | null }) {
  const data = points(weeks)
  const total = data.reduce((sum, point) => sum + point.call + point.meeting + point.email + point.note, 0)
  const summary = `Activiteit per week, ${data.length} weken: ${plural(total, 'registratie', 'registraties')}${trend ? `, ${plural(trend.contacts, 'contactmoment', 'contactmomenten')}, ${trendText(trend)}` : ''}.`
  return (
    <ChartCard
      title="Activiteit · 12 weken"
      definition="Gesprekken, meetings, e-mails en notities per week"
      empty={total === 0 ? { title: 'Nog geen activiteit vastgelegd.' } : null}
    >
      <div role="img" aria-label={summary} className="h-44 w-full">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 176 }}>
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="starts_on" tickFormatter={dayMonth} tick={{ fontSize: 12 }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip labelFormatter={(label) => `Week van ${dayMonth(String(label))}`} />
            {SERIES.map((serie) => (
              <Bar key={serie.key} dataKey={serie.key} name={serie.label} stackId="week" fill={serie.color} isAnimationActive={!reducedMotion()} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <table className="sr-only">
        <caption>Activiteit per week</caption>
        <thead>
          <tr>
            <th scope="col">Week</th>
            {SERIES.map((serie) => (
              <th key={serie.key} scope="col">
                {serie.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.week}>
              <th scope="row">{point.week}</th>
              {SERIES.map((serie) => (
                <td key={serie.key}>{point[serie.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm" aria-label="Legenda">
        {SERIES.map((serie) => (
          <li key={serie.key} className="flex items-center gap-1.5">
            <span aria-hidden className="size-2.5 rounded-sm" style={{ backgroundColor: serie.color }} />
            {serie.label}
          </li>
        ))}
      </ul>
      {trend ? (
        <div className="text-sm">
          <p className="font-semibold tabular-nums">{plural(trend.contacts, 'contactmoment', 'contactmomenten')}</p>
          <p className="text-muted-foreground">{trendText(trend)}</p>
        </div>
      ) : null}
    </ChartCard>
  )
}
